import { useNavigate, useSearchParams } from 'react-router-dom';
import { useNode } from '../contexts/NodeContext';
import {
  SearchPage as SharedSearchPage,
  type SearchPageMode,
  type SearchSubjectKey,
  reportModelSlug,
  subjectCropKey,
  type SearchEventRow,
} from '@trinetra/shared';
import { getNodeModelEvent, getNodeModelEventImage } from '../services/api.service';
import { EventDetailBody, EventDetailTitle } from './AlertInvestigationPage';

/**
 * Search — "who was wearing red today", across every AI model, for the selected site.
 *
 * Two things differ from the local node's copy of this page, and both come from the cloud having
 * no direct route to a node's storage:
 *
 *   • FRAMES are blob-fetched through the tunnel, one per card, lazily — `loadImage` instead of
 *     `imageFor`. The projection's `image_urls` are object-store URLs on the NODE's network and
 *     are not reachable from here, so handing them to the browser would show broken images.
 *   • The page is SITE-SCOPED. Search runs on one node; with none selected the shared page
 *     renders the empty state rather than an empty result that looks like "nothing happened".
 *
 * `key={nodeId}` remounts on a site switch, so no result, chip or facet from the previous site
 * can survive into the next one.
 *
 * THE RECORD opens in a modal over the results rather than at a route, because a search is a
 * position an operator is working through — navigating away to read one hit costs the query, the
 * answer and the scroll position. It renders the same `EventDetailBody` the report tables' modal
 * and the routed `/events/:model/:id` page render, so a record found by search is the same record
 * found by report. That route stays reachable through the modal's "Full page" button, for when a
 * record has to outlive the search that found it.
 */
export default function SearchPage({ subject }: {
  /**
   * Person or Vehicle — the page. Search and Track are a toggle INSIDE it, because they are two
   * readings of one query and navigating between them threw the query away.
   */
  subject?: SearchSubjectKey;
} = {}) {
  const { selectedNode } = useNode();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  /** Which reading the link asked for. `track` in the URL, `tracking` in the page. */
  const mode: SearchPageMode = params.get('mode') === 'track' ? 'tracking' : 'search';
  const nodeId = selectedNode?.nodeId || '';

  return (
    <SharedSearchPage
      /* Both pages are one component at one position in the tree, so without this React
         reuses the instance and Vehicle opens holding the person query that was on screen a
         moment ago. Keyed on the SUBJECT ONLY — putting the mode in here would remount on
         every Search/Track flip and throw away the query the toggle exists to preserve. */
      key={`${subject || 'any'}-${nodeId}`}
      mode={mode}
      subject={subject}
      /*
       * The mode lives in the URL so a reading can be linked and survives a reload, but it is
       * written ONE WAY: the page seeds from it and reports back, and `replace` keeps the
       * toggle out of the back button, which should step between pages rather than between two
       * views of one.
       */
      onModeChange={(next) => setParams((prev) => {
        const p = new URLSearchParams(prev);
        if (next === 'tracking') p.set('mode', 'track'); else p.delete('mode');
        return p;
      }, { replace: true })}
      // The two pages ARE two routes, so crossing between them is a navigation. The typed
      // sentence rides along in `?q=`: a notice that sent the operator to the right page with
      // an empty box would make them retype what they just typed.
      initialQuery={params.get('q') || undefined}
      /* A link may arrive knowing WHO it is about — the Watch List's Track button does. The
         subject travels as the page's own filter fields rather than as a sentence, because the
         parser only recognises names it has seen in the registry and would otherwise drop it
         silently, opening an unfiltered page that reads as the subject's whole trail. */
      initialFilters={(() => {
        const name = params.get('name');
        const plate = params.get('plate');
        if (!name && !plate) return undefined;
        return {
          ...(name ? { personName: name } : null),
          ...(plate ? { plate } : null),
        };
      })()}
      onSwitch={(to) => navigate(
        `/search/${to.subject}${to.query ? `?q=${encodeURIComponent(to.query)}` : ''}`,
      )}
      nodeKey={nodeId}
      loadImage={async (row: SearchEventRow) => {
        if (!nodeId || !row?.model_key || !row?.event_id) return null;
        try {
          /* THIS subject's crop, by KEY, falling back to the whole frame.
             A frame of six people is six cards, and asking for 'fullFrame' six times put the
             identical wide shot on all of them — six pictures of a crowd under six different
             descriptions. The node's image endpoint resolves an exact key before it tries its
             synonyms, so `person_crop_2` fetches person 2 and nothing else; a key the node
             cannot resolve 404s, which this already renders as a placeholder rather than a
             broken <img>. */
          const want = subjectCropKey(row) || 'fullFrame';
          const blob: any = await getNodeModelEventImage(nodeId, row.model_key, row.event_id, want);
          // A tunnel error (404 / timeout) still resolves as a Blob — of JSON or text — under
          // responseType:'blob'. Only real image bytes become an object URL; anything else is
          // a placeholder, not a broken <img>.
          if (!blob || (blob.type && !blob.type.startsWith('image/'))) return null;
          return URL.createObjectURL(blob);
        } catch {
          return null;
        }
      }}
      eventDetail={{
        // Node-scoped, unlike the local copy: every read crosses the tunnel to one site. Keyed by
        // (model, event_id) — what the projection stores, and the same lookup the routed event
        // page makes, so both resolve the identical record.
        load: (row: SearchEventRow) => getNodeModelEvent(nodeId, row.model_key, row.event_id),
        title: (event: any, row: SearchEventRow) => (
          <EventDetailTitle event={event} model={row.model_key} />
        ),
        render: (event: any, row: SearchEventRow, ctx) => (
          // `nodeId` is what lets the body stream its own snapshots back through the tunnel —
          // the local copy of this component needs no such thing, which is why the body is
          // supplied per host rather than shared.
          <EventDetailBody
            event={event}
            model={row.model_key}
            nodeId={nodeId}
            cameraNameMap={ctx.cameraNames}
            /* The identity the CARD showed, so the record cannot contradict the result that
               opened it. `face_identity` renders from this resolver, and with none supplied it
               called every matched face "Unknown" — a search for a name opening a record that
               denied it was them. `subject_label` is the projection's resolved identity (a
               name, or a plate); returning undefined lets the record's own
               `matched_person_name` answer instead. */
            personName={() => row.subject_label || undefined}
            /* The subject's attributes, rendered by the shared page from the PROJECTION row
               this result came from. The typed record fetched above carries them only as raw
               `metadata`, so deriving them here would let the modal disagree with the card it
               was opened from. */
            extraFields={ctx.attributeFacts}
            /* Open on THIS person's crop, not the wide shot. The card the reader clicked showed
               one person out of a frame that may hold seven; a record that opens on the crowd
               makes them find that person again in it. Null for a subjectless event or a model
               that ships no crops, and the record then opens on the full frame as it always did. */
            focusImageKey={row.crop_key || undefined}
            /* Which of the frame's subjects this record is — marks it in the breakdown below,
               so a reader can see the person they clicked among the others in the frame. */
            focusSubjectSeq={row.subject_seq}
            hint="← → move between results · ↑ ↓ move between this event’s frames"
          />
        ),
      }}
      // Longer HLS timeouts: this stream comes back through the tunnel.
      playbackTunnelled
      onOpenRecordings={(row) => {
        // camera + date are the params the Recordings page honours. A precise start/end is not
        // yet a thing it accepts, so passing one would look meaningful and be ignored — the
        // operator lands on the right camera and the right DAY, and seeks from there.
        const day = new Date(row.event_timestamp);
        const date = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
        const qs = new URLSearchParams({ camera: row.camera_id, date }).toString();
        navigate(`/recordings?${qs}`);
      }}
      onOpenEvent={(row: SearchEventRow) => {
        // Now the modal's "Full page" button rather than the card click. The cloud's event-detail
        // route is NOT node-scoped (`/events/:model/:eventId`) — it reads the selected site from
        // context, the same way the model reports' rows do. The segment is the SLUG, not the model
        // key: they differ for the snake_cased models (vehicle_crossing → vehicle-crossing) and a
        // key there is a dead link that silently redirects to the default report.
        if (row?.model_key && row?.event_id) {
          navigate(`/events/${reportModelSlug(row.model_key)}/${encodeURIComponent(row.event_id)}`);
        }
      }}
      emptyState={
        nodeId ? undefined : <div className="p-6 text-muted-foreground">Select a site to search its events.</div>
      }
    />
  );
}
